import * as yup from 'yup';
import moment from 'moment-timezone';
import { ANNOUNCEMENT_TYPE } from 'bs-unified-ui';
import {
  MESSAGE_WORD_LIMITS,
  DISPLAY_FORMAT,
  TIME_HOUR_DISPLAY_FORMAT,
  TIMEZONE,
} from '~/constants';
import { countWords } from '../textProcessing';

export const createAnnouncementValidationSchema = (
  isLiveAnnouncement?: boolean,
) =>
  yup
    .object()
    .shape({
      name: yup.string().required('Name is required'),
      type: yup.string().required('Type is required'),
      dismiss_behaviors: yup.string().required('Dismiss behavior is required'),
      message_description: yup
        .string()
        .required('Description is required')
        .test('word-count', function checkWordCount(value) {
          const { type } = this.parent as any;
          if (!value) return true;

          // Count words using shared utility function
          const wordCount = countWords(value);

          // Get max words from constant
          const maxWords =
            MESSAGE_WORD_LIMITS[type as keyof typeof MESSAGE_WORD_LIMITS] ||
            100;

          if (wordCount > maxWords) {
            return this.createError({
              message: `Description cannot exceed ${maxWords} words (currently ${wordCount} words)`,
            });
          }

          return true;
        }),
      message_title: yup.string().when('type', {
        is: (value: string) => value !== ANNOUNCEMENT_TYPE.GLOBAL_BANNER,
        then: (schema) => schema.required('Title is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
      image: yup.mixed().when('type', {
        is: (value: string) => value === ANNOUNCEMENT_TYPE.RICH_POP_UP,
        then: (schema) =>
          schema.test('image-required', 'Image is required', (value) => {
            // Valid if it's a File object or URL string
            if (value instanceof File || typeof value === 'string') {
              return true;
            }
            // Invalid if null, undefined, or other types
            return false;
          }),
        otherwise: (schema) => schema.notRequired(),
      }),
      start_date: yup
        .string()
        .required('Start time must have both date and hour'),
      start_hour: yup
        .string()
        .required('Start time must have both date and hour'),
      end_date: yup.string().required('End time must have both date and hour'),
      end_hour: yup.string().required('End time must have both date and hour'),
    })
    .test(
      'start-time-valid',
      'Start time must be current or future',
      function startTimeValid(values) {
        // Skip validation if this is a live announcement (being edited)
        if (isLiveAnnouncement) {
          return true;
        }

        const { start_date, start_hour, end_date, end_hour } = values;

        if (!start_date || !start_hour) {
          return true;
        }

        try {
          // Parse start time
          const startDateTime = `${start_date} ${start_hour}`;
          const startTime = moment.tz(
            startDateTime,
            `${DISPLAY_FORMAT} ${TIME_HOUR_DISPLAY_FORMAT}`,
            TIMEZONE,
          );

          if (!startTime.isValid()) {
            return this.createError({
              path: 'start_date',
              message: 'Start date format is invalid',
            });
          }

          // Get current time in Pacific timezone
          const now = moment.tz(TIMEZONE);

          // Check if start_time is current or future
          if (startTime.isBefore(now)) {
            return this.createError({
              path: 'start_hour',
              message: 'Start time must be current or future',
            });
          }

          // Check if end_time is greater than start_time
          if (end_date && end_hour) {
            const endDateTime = `${end_date} ${end_hour}`;
            const endTime = moment.tz(
              endDateTime,
              `${DISPLAY_FORMAT} ${TIME_HOUR_DISPLAY_FORMAT}`,
              TIMEZONE,
            );

            if (!endTime.isValid()) {
              return this.createError({
                path: 'end_date',
                message: 'End date format is invalid',
              });
            }

            if (endTime.isSameOrBefore(startTime)) {
              return this.createError({
                path: 'end_hour',
                message: 'End time must be later than start time',
              });
            }
          } else if (end_date && !end_hour) {
            return this.createError({
              path: 'end_hour',
              message: 'End time must have both date and hour',
            });
          } else if (!end_date && end_hour) {
            return this.createError({
              path: 'end_date',
              message: 'End time must have both date and hour',
            });
          }

          return true;
        } catch (error) {
          return this.createError({
            path: 'start_date',
            message: 'Invalid date/time format',
          });
        }
      },
    )
    .test(
      'end-time-valid-for-live',
      'End time must be greater than current time',
      function endTimeValidForLive(values) {
        // Only validate if this is a live announcement (being edited)
        if (!isLiveAnnouncement) {
          return true;
        }

        const { end_date, end_hour } = values;

        if (!end_date || !end_hour) {
          return true;
        }

        try {
          // Parse end time
          const endDateTime = `${end_date} ${end_hour}`;
          const endTime = moment.tz(
            endDateTime,
            `${DISPLAY_FORMAT} ${TIME_HOUR_DISPLAY_FORMAT}`,
            TIMEZONE,
          );

          if (!endTime.isValid()) {
            return this.createError({
              path: 'end_date',
              message: 'End date format is invalid',
            });
          }

          // Get current time in Pacific timezone
          const now = moment.tz(TIMEZONE);

          // Check if end_time is greater than current time
          if (endTime.isSameOrBefore(now)) {
            return this.createError({
              path: 'end_hour',
              message: 'End time must be greater than current time',
            });
          }

          return true;
        } catch (error) {
          return this.createError({
            path: 'end_date',
            message: 'Invalid date/time format',
          });
        }
      },
    );
